import authAxios from 'src/modules/shared/axios/authAxios';
// import axios from 'axios';
import { tenantSubdomain } from './tenantSubdomain';
import AuthCurrentTenant from '../auth/authCurrentTenant';
import config from 'src/config';
import SettingsService from '../settings/settingsService';

export default class TenantService {
  static async fetchAndApply() {
    const tenantUrl = tenantSubdomain.fromLocationHref();

    if (
      tenantSubdomain.isEnabled &&
      tenantSubdomain.isRootDomain
    ) {
      AuthCurrentTenant.clear();
      return;
    }

    // If there is a subdomain with the tenant url,
    // it must fetch the settings form that subdomain no matter
    // which one is on local storage
    if (tenantUrl) {
      let currentTenant;
      try {
        currentTenant = await this.findByUrl(tenantUrl);
      } catch (error) {
        console.error(error);
      }

      AuthCurrentTenant.set(currentTenant);

      if (!currentTenant) {
        window.location.href = `${config.frontendUrl.protocol}://${config.frontendUrl.host}`;
        return;
      }
    }

    const tenantId = AuthCurrentTenant.get();
    if (tenantId && !tenantUrl) {
      try {
        const currentTenant = await this.find(tenantId);

        AuthCurrentTenant.set(currentTenant);
      } catch (error) {
        console.error(error);
      }
    }

    SettingsService.applyThemeFromTenant();
  }

  static async update(id, data, type = '') {
    const body = {
      id,
      data,
      type,
    };

    const response = await authAxios.put(
      `/tenant/${id}`,
      body,
    );

    return response.data;
  }

  static async updatePermissions(id, data) {
    const body = {
      id,
      data,
    };

    const response = await authAxios.put(
      `/tenant/permissions/${id}`,
      body,
    );

    return response.data;
  }

  static async destroyAll(ids) {
    const params = {
      ids,
    };

    const response = await authAxios.delete(`/tenant`, {
      params,
    });

    return response.data;
  }

  static async create(data) {
    const body = {
      data,
    };

    const response = await authAxios.post(`/tenant`, body);

    return response.data;
  }

  static async acceptInvitation(
    token,
    forceAcceptOtherEmail = false,
  ) {
    const response = await authAxios.post(
      `/tenant/invitation/${token}/accept`,
      {
        forceAcceptOtherEmail,
      },
    );

    return response.data;
  }

  static async declineInvitation(token) {
    const params = null;

    const response = await authAxios.delete(
      `/tenant/invitation/${token}/decline`,
      {
        params,
      },
    );

    return response.data;
  }

  static async find(id) {
    const response = await authAxios.get(`/tenant/${id}`);
    return response.data;
  }

  static async findByUrl(url) {
    const response = await authAxios.get(`/tenant/url`, {
      params: { url },
    });
    return response.data;
  }

  static async findManyByName(businessName) {
    const response = await authAxios.get(`/tenant/find-by-name/${businessName}`);
    return response.data;
  }

  static async findAllClients(partnerId) {
    const response = await authAxios.get(`/tenant/clients/${partnerId}`);
    return response.data;
  }

  static async findAllLeadsOnlyClients() {
    const response = await authAxios.get(`/tenant/leads-only-clients`);
    return response.data;
  }

  static async getTokenStatuses() {
    const response = await authAxios.get(`/tenant/token-statuses`);
    return response.data;
  }

  static async getAllTargets() {
    const response = await authAxios.get('/get-all-targets');
    return response.data;
  }

  static async updateBusinessHours(day, data) {
    const tenantId = AuthCurrentTenant.get();
    const body = {
      tenantId,
      day,
      data
    };
    await authAxios.put('/tenant/business-hours/update', body);
    return;
  }

  static async findBusinessHours() {
    const tenantId = AuthCurrentTenant.get();
    const body = {
      tenantId,
    };
    const response = await authAxios.post('/tenant/business-hours/find', body);
    return response.data;
  }

  static async list(filter, orderBy, limit, offset) {
    const params = {
      filter,
      orderBy,
      limit,
      offset,
    };

    const response = await authAxios.get(`/tenant`, {
      params,
    });

    return response.data;
  }

  static async listAll() {
    const response = await authAxios.get(`/tenant/all`);

    return response.data;
  }

  static async archiveTenant(tenantId, isActive) {
    const body = {
      tenantId,
      isActive,
    };

    const response = await authAxios.post('/archive', body);

    return response.data;
  }

  static async partnerCreate(data: {
    tenantName: string;
    businessId: string;
    createNewUser: boolean;
    userEmail?: string;
    userPassword?: string;
    existingUserId?: string;
    accountFunded?: boolean;
    onboardingConfig?: {
      ccFeePercent?: number;
      setupFees?: Array<{ name: string; amount: number }>;
      monthlyServices?: Array<{ name: string; amount: number }>;
      campaignConfig?: {
        budget: number;
        campaignName?: string;
        maxBid: number;
        isAutoBid: boolean;
        pacingMethod: 'paced' | 'unpaced';
        endDate?: string;
      };
    };
  }) {
    const body = {
      data,
    };

    const response = await authAxios.post('/tenant/partner-create', body);

    return response.data;
  }

  static async partnerCreateSaaS(data: {
    tenantName: string;
    businessId: string;
    createNewUser: boolean;
    userEmail?: string;
    userPassword?: string;
    existingUserId?: string;
    featureAccess?: {
      hasBillingAccess: boolean;
      hasAIAccess: boolean;
      maxClients: number;
    };
    yelpReportingApiKey?: string;
    yelpAdsApiKey: string;
    yelpClientId?: string;
    yelpClientSecret?: string;
  }) {
    const body = {
      data,
    };

    const response = await authAxios.post('/tenant/partner-create-saas', body);

    return response.data;
  }

  static async fetchPartnerUsers() {
    const response = await authAxios.get('/tenant/partner-users');

    return response.data;
  }

  static async updateTenantName(tenantId: string, name: string) {
    const response = await authAxios.put(`/tenant/${tenantId}/name`, { name });

    return response.data;
  }
}
